import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, InfectionStatus } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    withCurrentEncounterId,
    isEmptyEncounterId,
    currentEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { InfectionStatusApi } from '../../infrastructure';
import { InfectionStatusActions } from '../actions';
import { selectInfectionAll } from '../selectors';

/**
 * Infections effects
 */
@Injectable()
export class InfectionStatusEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    infectionStatusAll$ = this.store.select(selectInfectionAll);

    /** Load Infection Status */
    loadInfectionStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InfectionStatusActions.loadInfectionStatus),
            withCurrentEncounterId(),
            concatMap(([, encounterId]) =>
                this.infectionStatusApi.getInfectionStatus(encounterId).pipe(
                    isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                    withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                    map(([data, changesMap, practitionersMap]) => {
                        const changes = changesMap['InfectionStatus'] || [];

                        if (changes.length) {
                            data = applyInstancesChanges(data, changes) as InfectionStatus[];
                        }

                        data = applyLastChangedByToPrescriptions(data, practitionersMap);

                        return InfectionStatusActions.loadInfectionStatusSuccess({ data });
                    }),
                    catchError(error => of(InfectionStatusActions.loadInfectionStatusFailure({ error }))),
                    takeUntil(isEmptyEncounterId(this.store)),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.succeededAction,
                ),
                withLatestFrom(this.infectionStatusAll$, this.changeLogMap$),
                tap(([, data, changesMap]) => {
                    const changes: ChangeLogEntry<InfectionStatus>[] = changesMap['InfectionStatus'] || [];

                    if (changes.length) {
                        const infectionStatuses = applyInstancesChanges(data, changes);

                        this.store.dispatch(InfectionStatusActions.upsertInfectionStatus({ data: infectionStatuses }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.infectionStatusAll$,
                    this.changeLogMap$,
                    this.practitionersFacade.practitionersMap$,
                ),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['InfectionStatus'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            InfectionStatusActions.addInfectionStatusChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     *
     * @param store
     * @param actions$
     * @param infectionStatusApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private infectionStatusApi: InfectionStatusApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
